import React from 'react';
import { TextField, Button, Box, Grid, Typography, MenuItem } from '@mui/material';
import { FormData } from '../types';

interface BusinessDetailsStepProps {
  data: Partial<FormData>;
  updateData: (data: Partial<FormData>) => void;
  onBack: () => void;
  onNext: () => void;
}

const companySizes = [
  { value: '1-10', label: '1-10 employees' },
  { value: '11-50', label: '11-50 employees' },
  { value: '51-200', label: '51-200 employees' },
  { value: '201-500', label: '201-500 employees' },
  { value: '501-1000', label: '501-1000 employees' },
  { value: '1000+', label: '1000+ employees' },
];

const revenueRanges = [
  { value: '0-1M', label: 'Less than $1M' },
  { value: '1M-10M', label: '$1M - $10M' },
  { value: '10M-50M', label: '$10M - $50M' },
  { value: '50M-100M', label: '$50M - $100M' },
  { value: '100M+', label: '$100M+' },
];

export const BusinessDetailsStep: React.FC<BusinessDetailsStepProps> = ({
  data,
  updateData,
  onBack,
  onNext,
}) => {
  const [errors, setErrors] = React.useState<Record<string, string>>({});

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!data.companySize) {
      newErrors.companySize = 'Company size is required';
    }
    if (!data.annualRevenue) {
      newErrors.annualRevenue = 'Annual revenue is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onNext();
    }
  };

  return (
    <Box component="form" onSubmit={handleNext}>
      <Typography variant="h6" gutterBottom>
        Business Details
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            label="Company Size"
            name="companySize"
            value={data.companySize || ''}
            onChange={(e) => updateData({ companySize: e.target.value })}
            error={!!errors.companySize}
            helperText={errors.companySize}
            required
          >
            <MenuItem value="">Select company size</MenuItem>
            {companySizes.map((size) => (
              <MenuItem key={size.value} value={size.value}>
                {size.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            label="Annual Revenue"
            name="annualRevenue"
            value={data.annualRevenue || ''}
            onChange={(e) => updateData({ annualRevenue: e.target.value })}
            error={!!errors.annualRevenue}
            helperText={errors.annualRevenue}
            required
          >
            <MenuItem value="">Select revenue range</MenuItem>
            {revenueRanges.map((range) => (
              <MenuItem key={range.value} value={range.value}>
                {range.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Company Website"
            name="website"
            placeholder="https://example.com"
            value={data.website || ''}
            onChange={(e) => updateData({ website: e.target.value })}
          />
        </Grid>
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button onClick={onBack}>Back</Button>
        <Button variant="contained" type="submit">
          Next
        </Button>
      </Box>
    </Box>
  );
};

